import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { sortVideos } from "@/utils/video";
import VideoCard from "@/components/base/VideoCard";

interface FilterGroup {
  label: string;
  key: "region" | "year" | "language";
  options: string[];
}

const filterGroups: FilterGroup[] = [
  { label: "地区", key: "region", options: ["中国大陆", "中国香港", "日本", "韩国", "美国", "英国", "挪威"] },
  { label: "年份", key: "year", options: ["2026", "2025", "2024"] },
  { label: "语言", key: "language", options: ["国语", "粤语", "日语", "韩语", "英语"] },
];

const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "play", label: "最多播放" },
  { value: "update", label: "最近更新" },
];

export default function CategoryPage() {
  const { categoryName } = useParams();
  const category = categories.find((item) => item.slug === categoryName);

  const [selected, setSelected] = useState<Record<string, string[]>>({
    region: [],
    year: [],
    language: [],
  });
  const [sort, setSort] = useState("latest");
  const [visibleCount, setVisibleCount] = useState(8);

  useEffect(() => {
    setSelected({ region: [], year: [], language: [] });
    setSort("latest");
    setVisibleCount(8);
  }, [categoryName]);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 色色视频`;
    } else {
      document.title = "分类未找到 - 色色视频";
    }
  }, [category]);

  const toggleTag = (key: string, value: string) => {
    setVisibleCount(8);
    setSelected((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const resetFilters = () => {
    setSelected({ region: [], year: [], language: [] });
    setSort("latest");
    setVisibleCount(8);
  };

  const activeCount = Object.values(selected).reduce((sum, list) => sum + list.length, 0);

  const filtered = useMemo(() => {
    if (!category) return [];
    const base = videos.filter((item) => item.category === category.slug);
    const result = base.filter((item) => {
      const regionOk = selected.region.length === 0 || selected.region.includes(item.tags.region);
      const yearOk = selected.year.length === 0 || selected.year.includes(item.tags.year);
      const languageOk =
        selected.language.length === 0 || selected.language.includes(item.tags.language);
      return regionOk && yearOk && languageOk;
    });
    return sortVideos(result, sort);
  }, [category, selected, sort]);

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-emotion-sad-line text-3xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-3xl text-foreground-950">分类未找到</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的分类不存在或已下线，返回首页看看其他内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line text-lg" aria-hidden="true"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const visibleVideos = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-base" aria-hidden="true"></i>
        <span>分类</span>
        <i className="ri-arrow-right-s-line text-base" aria-hidden="true"></i>
        <span className="text-foreground-900">{category.name}</span>
      </nav>

      <div className="mt-5 flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
          <i className={`${category.icon} text-3xl`} aria-hidden="true"></i>
        </span>
        <div>
          <h1 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
            {category.name}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500 md:text-base">
            {category.desc}
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-4">
          {filterGroups.map((group) => (
            <div key={group.key} className="flex flex-col gap-2 md:flex-row md:items-center">
              <span className="w-12 shrink-0 text-sm font-medium text-foreground-500">{group.label}</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelected((prev) => ({ ...prev, [group.key]: [] }));
                    setVisibleCount(8);
                  }}
                  className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    selected[group.key].length === 0
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                  }`}
                >
                  全部
                </button>
                {group.options.map((option) => {
                  const checked = selected[group.key].includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleTag(group.key, option)}
                      className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                        checked
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="flex flex-col gap-3 border-t border-background-200 pt-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">
                共 <strong className="text-primary-600">{filtered.length}</strong> 部视频
                {activeCount > 0 && <span className="ml-1 text-foreground-400">（已选 {activeCount} 个标签）</span>}
              </span>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-700 transition-colors hover:bg-secondary-200"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  重置筛选
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">排序</span>
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSort(option.value)}
                  className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    sort === option.value
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {visibleVideos.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
            <i className="ri-file-search-line text-3xl" aria-hidden="true"></i>
          </span>
          <h2 className="mt-5 font-heading text-2xl text-foreground-950">没有找到匹配的视频</h2>
          <p className="mt-2 text-sm text-foreground-500">当前筛选条件下暂无内容，试试重置筛选条件。</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line text-lg" aria-hidden="true"></i>
            重置筛选
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
            {visibleVideos.map((video, index) => (
              <VideoCard key={video.id} video={video} index={index} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + 8)}
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-add-line text-lg" aria-hidden="true"></i>
                加载更多（剩余 {filtered.length - visibleCount} 部）
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}